import React, { ChangeEvent, useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { Add, Delete, UploadFile } from "@mui/icons-material";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  SkillsCsvUploadAction,
  SkillsManualUploadAction,
} from "../../Redux/Actions/SkillAction";

type SkillEntryMode = "manual" | "csv";

interface CreateSkillsModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateSkillsModal: React.FC<CreateSkillsModalProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const dispatch = useDispatch<any>();
  const [mode, setMode] = useState<SkillEntryMode>("manual");
  const [manualSkills, setManualSkills] = useState<string[]>([""]);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);

  useEffect(() => {
    if (!open) {
      resetForm();
    }
  }, [open]);

  const resetForm = () => {
    setMode("manual");
    setManualSkills([""]);
    setCsvFile(null);
    setSubmitting(false);
  };

  const handleModeChange = (_: React.SyntheticEvent, newValue: SkillEntryMode) => {
    setMode(newValue);
  };

  const handleManualChange = (index: number, value: string) => {
    setManualSkills((prev) => {
      const nextSkills = [...prev];
      nextSkills[index] = value;
      return nextSkills;
    });
  };

  const addManualRow = () => {
    setManualSkills((prev) => [...prev, ""]);
  };

  const removeManualRow = (index: number) => {
    setManualSkills((prev) => {
      if (prev.length === 1) return prev;
      const nextSkills = [...prev];
      nextSkills.splice(index, 1);
      return nextSkills;
    });
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".csv")) {
      toast.error("Please upload a CSV file.");
      return;
    }
    setCsvFile(file);
  };

  const handleSubmit = async () => {
    if (mode === "manual") {
      await handleManualSubmit();
    } else {
      await handleCsvSubmit();
    }
  };

  const handleManualSubmit = async () => {
    const cleanedSkills = manualSkills
      .map((skill) => skill.trim())
      .filter((skill) => skill.length > 0);

    if (cleanedSkills.length === 0) {
      toast.error("Add at least one skill before submitting.");
      return;
    }

    try {
      setSubmitting(true);
      const response = await dispatch(SkillsManualUploadAction(cleanedSkills));
      setSubmitting(false);
      if (response?.status === true) {
        onSuccess();
        handleClose();
      }
    } catch (error) {
      setSubmitting(false);
    }
  };

  const handleCsvSubmit = async () => {
    if (!csvFile) {
      toast.error("Please choose a CSV file with a 'skills' column.");
      return;
    }
    try {
      setSubmitting(true);
      const response = await dispatch(SkillsCsvUploadAction(csvFile));
      setSubmitting(false);
      if (response?.status === true) {
        onSuccess();
        handleClose();
      }
    } catch (error) {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Create Skills</DialogTitle>
      <DialogContent>
        <Tabs
          value={mode}
          onChange={handleModeChange}
          sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
        >
          <Tab value="manual" label="Manual Entry" />
          <Tab value="csv" label="Bulk CSV Upload" />
        </Tabs>

        {mode === "manual" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Add skills one by one. Empty rows will be ignored.
            </Typography>
            {manualSkills.map((skill, index) => (
              <Stack direction="row" spacing={1} key={index} alignItems="center">
                <TextField
                  label={`Skill ${index + 1}`}
                  value={skill}
                  onChange={(e) => handleManualChange(index, e.target.value)}
                  fullWidth
                  placeholder="e.g., React"
                  size="small"
                />
                <IconButton
                  aria-label="remove"
                  onClick={() => removeManualRow(index)}
                  disabled={manualSkills.length === 1}
                >
                  <Delete />
                </IconButton>
              </Stack>
            ))}
            <Button
              startIcon={<Add />}
              onClick={addManualRow}
              sx={{ alignSelf: "flex-start" }}
            >
              Add another skill
            </Button>
          </Box>
        )}

        {mode === "csv" && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Upload a CSV with header &quot;skills&quot; and one skill per row.
            </Typography>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadFile />}
            >
              {csvFile ? csvFile.name : "Choose CSV file"}
              <input type="file" accept=".csv" hidden onChange={handleFileChange} />
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
        >
          {submitting ? "Submitting..." : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateSkillsModal;
